import { useState } from 'react';

import { Money } from '@betterment/core';

import { PerformanceLoadingState } from './loading-state/PerformanceLoadingState';

import { DateRangeOption } from '../../../../../../graphql/schema';
import { PerformanceSection } from '../../../../../../Shared/components-v2/charts/performance-chart/PerformanceSection';
import { DateRangeOptions } from '../../../../../../Shared/models/date-range-options';
import { urlBuilder } from '../../../../../../Shared/urlBuilder';
import { EnvelopeAccountPerformanceHistoryQuery } from '../../../graphql/envelopeAccountPerformanceHistory.generated';
import { useEnvelopePerformanceHistory } from '../../hooks/useEnvelopePerformanceHistory';

type ManagedAccountData = Extract<EnvelopeAccountPerformanceHistoryQuery['account'], { __typename: 'ManagedAccount' }>;

export const PerformanceChartSection = () => {
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>(DateRangeOption.AllTime);

  const { data: newData, loading, previousData } = useEnvelopePerformanceHistory(dateRangeOption);

  const data = newData ?? previousData;
  const hasValidData = data && 'performanceHistory' in data.account;
  const managedAccount = hasValidData ? (data.account as ManagedAccountData) : null;

  if (loading && !data) return <PerformanceLoadingState />;
  if (!hasValidData || !managedAccount) return null;

  const { performanceHistory } = managedAccount;

  const balance = managedAccount?.balance ?? Money(0);
  const earned = managedAccount?.performanceTotals.investing?.earned ?? Money(0);

  const isLoadingWithPreviousData = loading && !newData && !!previousData;

  const detailsUrl = urlBuilder.appAccountPerformance.absolute({
    accountId: managedAccount.id,
    returnTo: urlBuilder.appAccount.absolute({ accountId: managedAccount.id }),
    returnLabel: 'Account details',
  });

  return (
    <PerformanceSection
      balance={balance}
      earned={earned}
      detailsUrl={detailsUrl}
      timeSeries={performanceHistory.timeSeries}
      isLoading={isLoadingWithPreviousData}
      dateRangeOption={dateRangeOption}
      dateRangeOptions={DateRangeOptions.standard}
      onDateRangeChange={setDateRangeOption}
      disclosures={{
        recencyDescription: performanceHistory.recencyDescription,
        performanceDisclosure: data.disclosures.performanceDisclosure,
      }}
    />
  );
};
